"use client";

import React from "react";
import { OctagonAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Code as Code2, Compass, MapPin, RefreshCw, Search, ShieldCheck, Target, TrendingUp } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const challengePoints = [{
  id: 1,
  title: "High Spend & Low Yield (~$5,000 / Month)",
  description: "The business had been spending roughly $5,000 per month on prior digital marketing with a separate SEO specialist, but generated only ~14 leads per month."
}, {
  id: 2,
  title: "No Coherent Growth & Retention Strategy",
  description: "Lacked a structured, repeatable digital strategy to retain existing commercial clients while consistently attracting new commercial cleaning contracts."
}, {
  id: 3,
  title: "Falling Behind Search Competitors",
  description: "Competitors were dominating organic search and local visibility across key commercial service areas, capturing high-intent inquiries."
}];
const strategySteps = [{
  step: "01",
  iconName: "Search",
  title: "Comprehensive Website Audit",
  summary: "Conducted an exhaustive technical and structural audit to identify crawling issues, architecture bottlenecks, and conversion barriers."
}, {
  step: "02",
  iconName: "MapPin",
  title: "Google Business Profile Setup & Optimization",
  summary: "Built out, claimed, and thoroughly optimized the Google Business Profile to capture local commercial cleaning searches and inquiries."
}, {
  step: "03",
  iconName: "Code2",
  title: "Schema Markup Implementation",
  summary: "Deployed structured data to clarify service offerings, regional coverage, and local entity signals directly to search engines."
}, {
  step: "04",
  iconName: "Compass",
  title: "Comprehensive Keyword Research",
  summary: "Mapped high-intent commercial keywords, B2B contract terms, and local search queries to target qualified corporate decision-makers."
}];
export default function Strategy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="casestudychallengestrategy" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Case Study Deep Dive
          </Badge>
          <h2 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            From Stagnant Marketing Spend to a High-ROI Strategy
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            How I audited prior digital marketing inefficiencies and implemented a focused, 4-part local SEO foundation to reliably win and retain commercial cleaning clients.
          </p>
        </div>

        {}
        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-6">
            <Card className="h-full border border-border/80 bg-card shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                    <AlertTriangle className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-destructive">
                      The Challenge
                    </span>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      Prior Spend &amp; Underperforming Results
                    </CardTitle>
                  </div>
                </div>
                <CardDescription className="pt-2 text-sm text-muted-foreground">
                  The client invested ~$5,000/month with a separate SEO specialist but only saw ~14 leads/month and struggled to retain or attract clients.
                </CardDescription>
              </CardHeader>
              <Separator />
              <CardContent className="space-y-4 pt-6">
                {challengePoints.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3 rounded-lg border border-border/40 bg-background/50 p-3.5">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive/80" />
                    <div>
                      <h3 className="font-default text-sm font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-6">
            <Card className="h-full border border-border/80 bg-card shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <TrendingUp className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                      Primary Objective
                    </span>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      Foundational Online Presence with Clear ROI
                    </CardTitle>
                  </div>
                </div>
                <CardDescription className="pt-2 text-sm text-muted-foreground">
                  Refocus digital strategy around winning new clients, retaining existing accounts, and establishing long-term search authority.
                </CardDescription>
              </CardHeader>
              <Separator />
              <CardContent className="space-y-4 pt-6">
                <div className="rounded-lg bg-secondary/60 p-4 border border-border/40">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <ShieldCheck className="size-4" />
                    <span>Direct Consultant Accountability</span>
                  </div>
                  <p className="mt-1.5 text-xs text-foreground/80 leading-relaxed">
                    {"Working directly with me ensured that every recommendation, technical audit fix, and keyword deployment was executed with clear commercial objectives."}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-xs text-muted-foreground">
                      <strong className="font-semibold text-foreground">Gain New Clients:</strong>{" "}
                      Capture high-intent commercial cleaning searches across key local and regional service markets.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-xs text-muted-foreground">
                      <strong className="font-semibold text-foreground">Retain Existing Clients:</strong>{" "}
                      Strengthen digital brand trust, credibility, and service accessibility for current business relationships.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-xs text-muted-foreground">
                      <strong className="font-semibold text-foreground">Measurable Commercial ROI:</strong>{" "}
                      Build a dependable inbound pipeline to generate qualified commercial contract inquiries cost-effectively.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {}
        <div className="mt-16">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                The Strategic Roadmap
              </span>
              <h3 className="font-default mt-1 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                4-Part Strategic Approach
              </h3>
            </div>
            <p className="max-w-md text-xs text-muted-foreground sm:text-sm">
              A methodical SEO rollout built to resolve technical barriers and systematically capture commercial search intent.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {strategySteps.map((stepItem, i) => <motion.div key={stepItem.step} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card p-5 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-extrabold text-muted-foreground/40 font-mono">
                      {stepItem.step}
                    </span>
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      {stepItem.iconName === "Search" && <Search className="size-4" />}
                      {stepItem.iconName === "MapPin" && <MapPin className="size-4" />}
                      {stepItem.iconName === "Code2" && <Code2 className="size-4" />}
                      {stepItem.iconName === "Compass" && <Compass className="size-4" />}
                    </div>
                  </div>
                  <h4 className="font-default mt-4 text-base font-semibold text-card-foreground">
                    {stepItem.title}
                  </h4>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {stepItem.summary}
                  </p>
                </Card>
              </motion.div>)}
          </motion.div>

          {}
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-border bg-card/60 p-4 text-xs text-muted-foreground">
            <RefreshCw className="mt-0.5 size-4 shrink-0 text-primary" />
            <p className="leading-relaxed">
              <strong className="font-medium text-foreground">Iterative Strategy &amp; Expansion:</strong>{" "}
              This 4-part foundational strategy was followed by iterative monthly refinement and continuous keyword expansion to scale visibility across new commercial sectors.
            </p>
          </div>
        </div>

        {}
        <div className="mt-12 rounded-xl border border-border bg-muted/40 p-6 md:p-8">
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Target className="size-4" />
                <span>Proven Local SEO Execution</span>
              </div>
              <h4 className="font-default mt-1 text-lg font-bold text-foreground sm:text-xl">
                Structured Audits &amp; Targeted Schema Implementation
              </h4>
              <p className="mt-2 text-xs text-muted-foreground sm:text-sm leading-relaxed">
                By combining comprehensive auditing with structured schema and high-intent keyword mapping, we established a dependable organic funnel that continuously attracts corporate cleaning inquiries.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:justify-end">
              <Button asChild variant="default" size="default">
                <Link to="/CaseStudiesPage">
                  <span>View Full Case Study</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default">
                <Link to="/ContactPage">
                  <span>Discuss Your Project</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}